import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { ReactNode } from 'react';

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';
export type AlertType =
  | 'order_lock'
  | 'signature_lock'
  | 'order_error'
  | 'general'
  | 'download'
  | 'preview';

export interface FileManagerAlert {
  id: string;
  type: AlertType;
  timeout: number;
  createdAt: number;
  severity: AlertSeverity;
  message: ReactNode;
  icon?: ReactNode;
  read: boolean;
  completed: boolean;
  totalElapsedTime: number;
}

interface FileManagerAlertsState {
  alerts: FileManagerAlert[];
}

const initialState: FileManagerAlertsState = {
  alerts: [],
};

const fileManagerAlertsSlice = createSlice({
  name: 'fileManagerAlerts',
  initialState,
  reducers: {
    addAlert: (
      state,
      action: PayloadAction<
        Omit<
          FileManagerAlert,
          'id' | 'createdAt' | 'read' | 'completed' | 'totalElapsedTime'
        >
      >
    ) => {
      const alert: FileManagerAlert = {
        ...action.payload,
        id: uuidv4(),
        createdAt: Date.now(),
        timeout: action.payload.timeout || 15000, // Default to 15 seconds
        read: false,
        completed: false,
        totalElapsedTime: 0,
      };
      state.alerts.push(alert);
    },
    updateAlert: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<Omit<FileManagerAlert, 'id' | 'createdAt'>>;
      }>
    ) => {
      const alert = state.alerts.find((a) => a.id === action.payload.id);
      if (alert) {
        Object.assign(alert, action.payload.updates);
        // Reset createdAt to restart the timer
        alert.createdAt = Date.now();
      }
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(
        (alert) => alert.id !== action.payload
      );
    },
    clearAllAlerts: (state) => {
      state.alerts = state.alerts.map((alert) => ({
        ...alert,
        completed: true,
      }));
    },
    clearAlertsByType: (state, action: PayloadAction<AlertType>) => {
      state.alerts = state.alerts.map((alert) =>
        alert.type === action.payload ? { ...alert, completed: true } : alert
      );
    },
    markAlertAsRead: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert) {
        alert.read = true;
      }
    },
    markAlertAsCompleted: (state, action: PayloadAction<string>) => {
      const alert = state.alerts.find((a) => a.id === action.payload);
      if (alert) {
        alert.completed = true;
      }
    },
    updateAlertElapsedTime: (
      state,
      action: PayloadAction<{ id: string; elapsedTime: number }>
    ) => {
      const alert = state.alerts.find((a) => a.id === action.payload.id);
      if (alert) {
        alert.totalElapsedTime = action.payload.elapsedTime;
      }
    },
  },
});

export const {
  addAlert,
  updateAlert,
  removeAlert,
  clearAllAlerts,
  clearAlertsByType,
  markAlertAsRead,
  markAlertAsCompleted,
  updateAlertElapsedTime,
} = fileManagerAlertsSlice.actions;

export default fileManagerAlertsSlice.reducer;
